import React, { useRef, useState } from "react";
import { Upload, X, Image, Loader } from "lucide-react";
import { uploadFile, BASE } from "../../services/api";

function resolveUrl(url) {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${BASE}${url}`;
}

export default function ImageUpload({ value, onChange, onThumbChange, label = "Fotka zariadenia", compact = false, uploadFn }) {
    const inputRef  = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError]       = useState("");
    const [sizeInfo, setSizeInfo] = useState(null); // { original, saved }

    const doUpload = uploadFn ?? uploadFile;

    const handleFile = async (file) => {
        if (!file) return;
        setError("");
        setSizeInfo(null);
        const originalKB = Math.round(file.size / 1024);
        setUploading(true);
        try {
            const data = await doUpload(file);
            const url = data.url ?? data;
            onChange(url);
            if (onThumbChange && data.thumb_url) onThumbChange(data.thumb_url);
            if (data.url && originalKB > 50) setSizeInfo({ original: originalKB });
        } catch (e) {
            setError(e.message);
        } finally {
            setUploading(false);
        }
    };

    const onInputChange = (e) => handleFile(e.target.files[0]);

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        handleFile(e.dataTransfer.files[0]);
    };

    const previewUrl = resolveUrl(value);

    if (compact) {
        return (
            <div className="flex gap-2 items-center">
                <input
                    type="text" value={value} onChange={e => onChange(e.target.value)}
                    placeholder="URL obrázka…"
                    className="flex-1 h-8 px-3 rounded bg-nova-black border border-white/8 text-white text-xs placeholder-zinc-700 focus:outline-none focus:border-nova-cyan/50 transition-colors"
                />
                <button type="button" onClick={() => inputRef.current?.click()}
                    className="flex items-center gap-1 px-3 h-8 rounded bg-nova-surface border border-white/10 text-zinc-400 hover:text-white text-xs font-mono transition-colors shrink-0">
                    {uploading ? <Loader className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    Nahrať
                </button>
                <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onInputChange} />
                {error && <span className="text-red-400 text-xs">{error}</span>}
            </div>
        );
    }

    return (
        <div>
            <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
                {label}
            </label>

            {previewUrl ? (
                <div className="relative rounded-lg overflow-hidden border border-white/10 bg-nova-black">
                    <img
                        src={previewUrl}
                        alt="preview"
                        className="w-full h-40 object-cover"
                    />
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        className="absolute top-2 right-2 p-1 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="absolute bottom-2 right-2 flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-black/60 text-white text-xs font-mono hover:bg-black/80 transition-colors"
                    >
                        <Upload className="w-3 h-3" /> Zmeniť
                    </button>
                </div>
            ) : (
                <div
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    onClick={() => inputRef.current?.click()}
                    className={`relative flex flex-col items-center justify-center gap-2 h-32 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                        dragging
                            ? "border-nova-cyan/60 bg-nova-cyan/5"
                            : "border-white/10 bg-nova-black/40 hover:border-white/25 hover:bg-white/2"
                    }`}
                >
                    {uploading ? (
                        <>
                            <Loader className="w-6 h-6 text-nova-cyan animate-spin" />
                            <span className="text-zinc-500 text-xs font-mono">Nahrávam…</span>
                        </>
                    ) : (
                        <>
                            <Image className="w-6 h-6 text-zinc-600" />
                            <span className="text-zinc-500 text-xs font-mono text-center px-4">
                                Kliknite alebo pretiahnite súbor
                            </span>
                            <span className="text-zinc-700 text-[10px] font-mono">JPG, PNG, WEBP</span>
                        </>
                    )}
                </div>
            )}

            {/* URL fallback input */}
            <input
                type="text"
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder="alebo vložte URL obrázka…"
                className="mt-2 w-full h-8 px-3 rounded bg-nova-black border border-white/8 text-white text-xs placeholder-zinc-700 focus:outline-none focus:border-nova-cyan/50 transition-colors"
            />

            {error && <p className="mt-1 text-red-400 text-xs font-mono">{error}</p>}
            {sizeInfo && (
                <p className="mt-1 text-emerald-500 text-[10px] font-mono">
                    ✓ Skonvertované do WebP — pôvodná veľkosť: {sizeInfo.original} KB
                </p>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={onInputChange}
            />
        </div>
    );
}
